import { createStyles, Group, Menu, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { trackEvent } from '@repo/common/analytics';
import { KeyboardShortcut } from '@repo/common/components/KeyboardShortcut/KeyboardShortcut';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { Icon, IconButton, Text } from '@repo/foundations';
import { useKeyPress } from 'ahooks';
import { memo, useCallback, useState } from 'react';
import { useAuthUser, useSuspenseFeatureAccess } from '../../../api';
import { GLOBAL_SHORTCUTS } from '../../../constants/shortcuts';
import { isInputFocused } from '../../../hooks/useSidebarShortcuts';
import { useSuspenseFeatureAccessCheck } from '../../../utils/authorization/accessMessages';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { useFeatureFlags } from '../../../utils/featureFlags';
import AnnouncementModal from '../../Announcement/AnnouncementModal';
import { DataAccessRequestCreateModal } from '../../DataAccessRequest/DataAccessRequestCreateModal/DataAccessRequestCreateModal';
import { entityModalStore } from '../../EntityModal/store';
import { closeAllModals, openModal } from '../../ModalManager/events';

const useStyles = createStyles(() => ({}));

interface SideBarCreateMenuProps {
	isViewerUser: boolean;
}

export const SideBarCreateMenu = memo(
	({ isViewerUser }: SideBarCreateMenuProps) => {
		const { theme } = useStyles();
		const { collectionsAccess } = useSuspenseFeatureAccess();
		const { user, workspace } = useAuthUser();
		const [opened, setOpened] = useState(false);

		const { biDashboardsEnabled } = useFeatureFlags();
		const { restrictionMessage: biDashboardsRestrictionMessage } =
			useSuspenseFeatureAccessCheck({
				v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
				v2Permission: 'Dashboards.Manage',
				isFeatureIncludedInPlan: true,
				isAlwaysIncludedFeature: true,
			});

		const showCreateDashboard =
			biDashboardsEnabled && !biDashboardsRestrictionMessage;

		const { hasPermission: canCreateAnnouncement } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Announcements.Write',
		});

		const [
			announcementModalOpen,
			{ close: closeAnnouncementModal, open: openAnnouncementModal },
		] = useDisclosure(false);

		const handleCreateAnnouncementClick = useCallback(() => {
			openAnnouncementModal();
			trackEvent('inbox/new_announcement_toggle/click', {}, user, workspace);
		}, [openAnnouncementModal, user, workspace]);

		const handleCreateClick = useCallback(
			(type: EntityType) => () => {
				entityModalStore.open({
					type,
					metadata: {
						owners: [user.id],
					},
				});
			},
			[user.id]
		);

		const handleOpenModal = () => {
			openModal({
				title: 'Request access',
				children: <DataAccessRequestCreateModal onClose={closeAllModals} />,
			});
		};

		// Handle keyboard shortcut to open/close the menu
		useKeyPress(GLOBAL_SHORTCUTS.CREATE_MENU, (event) => {
			// Check if an input element is currently focused to prevent shortcuts during typing
			const isInput = isInputFocused();
			if (isInput) return;

			// Don't trigger if any modifier keys are pressed (prevents cmd+c, ctrl+c, etc.)
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
				return;
			}

			setOpened((prev) => !prev);
		});

		return (
			<Menu opened={opened} onChange={setOpened}>
				<Menu.Target>
					<Tooltip
						label={
							<KeyboardShortcut keys={['C']}>
								<Text size="xs">Create</Text>
							</KeyboardShortcut>
						}
						position="top"
					>
						<IconButton iconName="plus" variant="tertiary" />
					</Tooltip>
				</Menu.Target>
				<Menu.Dropdown>
					<Group p={`${theme.spacing['2xs']} ${theme.spacing.xs}`}>
						<Text color="text/secondary/default" size="xs">
							Create
						</Text>
					</Group>
					{collectionsAccess && !isViewerUser && (
						<Menu.Item onClick={handleCreateClick(EntityType.collection)}>
							<Group spacing="2xs">
								<Icon name="folder" color="icon/primary/default" />
								<Text size="sm">Collection</Text>
							</Group>
						</Menu.Item>
					)}
					{!isViewerUser && (
						<Menu.Item onClick={handleCreateClick(EntityType.glossary)}>
							<Group spacing="2xs">
								<Icon name="book" color="icon/primary/default" />
								<Text size="sm">Glossary term</Text>
							</Group>
						</Menu.Item>
					)}
					{!isViewerUser && (
						<Menu.Item onClick={handleCreateClick(EntityType.document)}>
							<Group spacing="2xs">
								<Icon name="file" color="icon/primary/default" />
								<Text size="sm">Document</Text>
							</Group>
						</Menu.Item>
					)}
					{showCreateDashboard && (
						<Menu.Item onClick={handleCreateClick(EntityType.bi_dashboard)}>
							<Group spacing="2xs">
								<Icon
									name="deviceDesktopAnalytics"
									color="icon/primary/default"
								/>
								<Text size="sm">Dashboard</Text>
							</Group>
						</Menu.Item>
					)}
					{canCreateAnnouncement && (
						<Menu.Item onClick={handleCreateAnnouncementClick}>
							<Group spacing="2xs">
								<Icon name="speakerphone" color="icon/primary/default" />
								<Text size="sm">Announcement</Text>
							</Group>
						</Menu.Item>
					)}
					<Menu.Item onClick={handleCreateClick(EntityType.question)}>
						<Group spacing="2xs">
							<Icon name="messageCircleQuestion" color="icon/primary/default" />
							<Text size="sm">Question</Text>
						</Group>
					</Menu.Item>
					<Menu.Item onClick={handleOpenModal}>
						<Group spacing="2xs">
							<Icon name="lockOpen" color="icon/primary/default" />
							<Text size="sm">Access request</Text>
						</Group>
					</Menu.Item>
				</Menu.Dropdown>
				{announcementModalOpen && (
					<AnnouncementModal
						opened={announcementModalOpen}
						close={closeAnnouncementModal}
					/>
				)}
			</Menu>
		);
	}
);
